import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { articleAPI } from '../services/api';
import './SearchPage.css';

function SearchPage() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (query) {
      fetchSearchResults();
    } else {
      setLoading(false);
    }
  }, [query]);

  const fetchSearchResults = async () => {
    try {
      setLoading(true);
      const response = await articleAPI.searchArticles({ q: query, limit: 50 });

      // Backend returns { query, category, count, articles: [...] }
      // So we need response.data.articles, not response.data
      const resultsArray = Array.isArray(response.data.articles)
        ? response.data.articles
        : (Array.isArray(response.data) ? response.data : []);

      setArticles(resultsArray);
    } catch (error) {
      console.error('Error searching articles:', error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      crypto: '#f39c12',
      ai: '#3498db',
      politics: '#e74c3c'
    };
    return colors[category] || '#666';
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    return `${Math.floor(seconds / 86400)} days ago`;
  };

  if (!query) {
    return (
      <div className="search-page">
        <div className="search-container">
          <div className="no-results">
            <h2>No search query</h2>
            <p>Please enter a search term</p>
            <Link to="/" className="back-button">← Back to Home</Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Searching for "{query}"...</p>
      </div>
    );
  }

  return (
    <div className="search-page">
      <div className="search-container">
        <div className="search-header">
          <h1 className="search-title">Search Results</h1>
          <p className="search-query">
            Showing {articles.length} results for "<strong>{query}</strong>"
          </p>
        </div>

        {articles.length === 0 ? (
          <div className="no-results">
            <h2>No articles found</h2>
            <p>Try different keywords or browse our categories</p>
            <div className="search-suggestions">
              <Link to="/crypto" className="suggestion-link">Browse Crypto</Link>
              <Link to="/ai" className="suggestion-link">Browse AI</Link>
              <Link to="/politics" className="suggestion-link">Browse Politics</Link>
            </div>
          </div>
        ) : (
          <div className="search-results">
            {[...articles]
              .sort((a, b) => {
                // Breaking news first
                if (a.is_breaking && !b.is_breaking) return -1;
                if (!a.is_breaking && b.is_breaking) return 1;
                // Then by date (newest first)
                return new Date(b.published_at) - new Date(a.published_at);
              })
              .map(article => (
              <article key={article.id} className="search-result-item">
                <Link to={`/article/${article.id}`} className="result-image-link">
                  <img
                    src={article.image_url || 'https://via.placeholder.com/200x150?text=News'}
                    alt={article.title}
                    className="result-image"
                    onError={(e) => e.target.src = 'https://via.placeholder.com/200x150?text=News'}
                  />
                </Link>
                <div className="result-content">
                  <div className="result-badges">
                    <span
                      className="category-badge"
                      style={{ backgroundColor: getCategoryColor(article.category) }}
                    >
                      {article.category?.toUpperCase()}
                    </span>
                    {article.is_breaking && (
                      <span className="breaking-badge-small">🔥 BREAKING</span>
                    )}
                  </div>
                  <Link to={`/article/${article.id}`}>
                    <h3 className="result-title">{article.title}</h3>
                  </Link>
                  <p className="result-summary">
                    {(article.ai_summary || article.summary || '').substring(0, 200)}...
                  </p>
                  <div className="result-meta">
                    <span className="result-source">{article.source_name}{article.source_name && ' via FocusView.ai'}</span>
                    <span className="meta-separator">•</span>
                    <span className="result-time">{formatTimeAgo(article.published_at)}</span>
                    {article.view_count > 0 && (
                      <>
                        <span className="meta-separator">•</span>
                        <span className="result-views">{article.view_count} views</span>
                      </>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchPage;